import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StaffLayout from '../../components/StaffLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Search, UserPlus, KeyRound, FileText, Copy, Check } from 'lucide-react';
import { api, errMsg, useApi } from '../../lib/api';
import { STAFF_TABS, money } from '../../lib/constants';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { CurrencyBalances } from '../../components/CurrencyBalances';
import { CurrencySwitcher, useStaffCurrency } from '../../components/CurrencySwitcher';
import { CustomerManagementDialog } from '../../components/CustomerManagementDialog';
import { UserRoundCog } from 'lucide-react';
import { searchCustomers } from '../../lib/customerSearch';
import { useCustomers } from '../../hooks/useCustomers';

export default function CustomersListPage() {
  const [currency, setCurrency] = useStaffCurrency();
  const [q, setQ] = useState('');
  const { user } = useAuth();
  const { data: customers, reload } = useCustomers();
  const filtered = searchCustomers(customers, q, true);
  const [selected, setSelected] = useState(null);

  return (
    <StaffLayout tabs={STAFF_TABS}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-slate-800">Customer List</h1>
        <Link to="/app/customers/new"><Button data-testid="customers-add-button" className="bg-emerald-600 hover:bg-emerald-700 text-white"><UserPlus className="h-4 w-4 mr-2" /> Add Customer</Button></Link>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-100 flex flex-wrap gap-4 justify-between">
            <CurrencySwitcher currency={currency} onChange={setCurrency} />
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="customers-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, account or phone" className="pl-9" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-slate-700 text-white">
                <tr>{['Account No', 'Name', 'Phone', 'Email', 'Balances', 'Since', 'Actions'].map(h => <th key={h} className="text-left px-4 py-2.5 text-xs uppercase tracking-wide font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id} className={i % 2 ? 'bg-emerald-50/40' : 'bg-white'} data-testid={`customer-row-${c.accountNo}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{c.accountNo}</td>
                    <td data-testid={`customer-name-${c.accountNo}`} className="px-4 py-2.5 font-medium text-slate-800">{c.name}{q.trim() && <div data-testid={`customer-usage-${c.accountNo}`} className="text-xs text-slate-500 font-normal">{c.transactionCount || 0} posted transactions</div>}{c.deletionPending && <div data-testid={`customer-deletion-pending-${c.accountNo}`} className="text-xs text-rose-700">Deletion incomplete</div>}</td>
                    <td className="px-4 py-2.5 text-slate-600">{c.phone}</td>
                    <td className="px-4 py-2.5 text-slate-600">{c.email}</td>
                    <td className="px-4 py-2.5 font-semibold text-emerald-700"><CurrencyBalances currency={currency} balances={c.balances} prefix={`customer-balance-${c.accountNo}`} /></td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        <Link data-testid={`customer-statement-${c.accountNo}`} to={`/app/statement?accountNo=${c.accountNo}`} title="Statement" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"><FileText className="h-4 w-4" /></Link>
                        <button data-testid={`customer-manage-${c.accountNo}`} title="Customer details" aria-label={`Customer details for ${c.name}`} onClick={() => setSelected(c)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-amber-700 hover:bg-amber-50"><UserRoundCog className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {customers && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No customers</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      {selected && <CustomerManagementDialog customer={selected} admin={user?.role === 'Admin'} onClose={() => setSelected(null)} onUpdated={reload} />}
    </StaffLayout>
  );
}

export function ResetPasswordDialog({ customer, onClose, endpoint }) {
  const { toast } = useToast();
  const [pw, setPw] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const close = () => { setPw(''); setResult(null); setCopied(false); onClose(); };

  const submit = async (generate) => {
    setBusy(true);
    try {
      const url = endpoint ? endpoint(customer) : `/customers/${customer.accountNo}/reset-password`;
      const { data } = await api.post(url, { password: generate ? null : pw });
      setResult(data.password);
    } catch (e) { toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!customer} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-md" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password — {customer?.name}</DialogTitle>
          <DialogDescription>{customer?.accountNo || customer?.username}. Share the new password with the account holder in person.</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs uppercase font-semibold text-emerald-800/70">New password</div>
            <div className="flex items-center justify-between gap-3 mt-1">
              <div className="font-mono text-xl font-bold text-emerald-900" data-testid="reset-password-result">{result}</div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(result); setCopied(true); }}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label>New password (min 4 characters)</Label>
            <Input data-testid="reset-password-input" value={pw} onChange={e => setPw(e.target.value)} placeholder="Type a password or generate one" />
          </div>
        )}
        <DialogFooter className="gap-2">
          {result ? <Button data-testid="reset-password-done" onClick={close} className="bg-emerald-600 hover:bg-emerald-700 text-white">Done</Button> : (
            <>
              <Button data-testid="reset-password-generate" variant="outline" disabled={busy} onClick={() => submit(true)}>Generate temporary</Button>
              <Button data-testid="reset-password-submit" disabled={busy || pw.length < 4} onClick={() => submit(false)} className="bg-emerald-600 hover:bg-emerald-700 text-white">Set password</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
